import { z } from 'zod';

import {
  ExportKind,
  ImportKind,
  ImportOnExisting,
  type PostAdminV1StoresStoreIdExportsBody,
} from '@nucleus-commerce/api-client/admin';

/** Only the orders export takes a placed-date range; the other kinds dump everything. */
export const exportFormSchema = z
  .object({
    kind: z.enum(ExportKind),
    from: z.string(),
    to: z.string(),
  })
  .superRefine((v, ctx) => {
    if (v.kind !== 'orders_csv') return;
    if (v.from && v.to && v.to < v.from) {
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: 'importsExports:validation.toBeforeFrom',
      });
    }
  });

export type ExportFormValues = z.infer<typeof exportFormSchema>;

export function toExportBody(values: ExportFormValues): PostAdminV1StoresStoreIdExportsBody {
  if (values.kind !== 'orders_csv') return { kind: values.kind };
  const filters = {
    ...(values.from ? { from: values.from } : {}),
    ...(values.to ? { to: values.to } : {}),
  };
  return Object.keys(filters).length > 0 ? { kind: values.kind, filters } : { kind: values.kind };
}

export const importFormSchema = z.object({
  kind: z.enum(ImportKind),
  onExisting: z.enum(ImportOnExisting),
  file: z
    .custom<File | null>((value) => value instanceof File, {
      error: 'importsExports:validation.fileRequired',
    })
    .refine((file) => file !== null && file.size > 0, {
      error: 'importsExports:validation.fileEmpty',
    }),
});

export type ImportFormInput = z.input<typeof importFormSchema>;
export type ImportFormValues = z.output<typeof importFormSchema>;

/** Only products can hit an existing record by handle; other kinds leave the choice unsent. */
export function importOnExisting(values: ImportFormValues): ImportOnExisting | undefined {
  return values.kind === 'products_csv' ? values.onExisting : undefined;
}
